import { useState, useEffect } from "react";
import { AlertTriangle, MapPin, Layers, Users, ChevronDown, ChevronUp } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from "recharts";

const AXIS_TICK = { fontSize: 11 };
const AXIS_STROKE = "hsl(240, 5%, 65%)";
const GRID_STROKE = "hsl(240, 5%, 17%)";
const TOOLTIP_STYLE = {
  contentStyle: { backgroundColor: 'hsl(240, 6%, 7%)', border: '1px solid hsl(240, 5%, 17%)', borderRadius: '8px' },
  isAnimationActive: false,
  wrapperStyle: { pointerEvents: 'none' },
};

const getCoverageColor = (pct) => {
  if (pct >= 80) return "#22c55e";
  if (pct >= 50) return "#f59e0b";
  if (pct >= 25) return "#f97316";
  return "#ef4444";
};

const getCoverageLabel = (pct) => {
  if (pct >= 80) return "Strong";
  if (pct >= 50) return "Moderate";
  if (pct >= 25) return "Weak";
  return "Critical";
};

export const DataGapsCard = () => {
  const [gaps, setGaps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchGaps = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/data-gaps`);
        const data = await res.json();
        setGaps(data);
      } catch (err) {
        void 0; // error handled silently
      } finally {
        setLoading(false);
      }
    };
    fetchGaps();
  }, []);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-5" data-testid="data-gaps-loading">
        <h3 className="text-base font-semibold mb-4 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          Dataset Blind Spots
        </h3>
        <div className="h-32 flex items-center justify-center text-muted-foreground">
          Analyzing coverage gaps...
        </div>
      </div>
    );
  }

  if (!gaps) return null;

  const sectorData = gaps.sector_gaps.map(s => ({
    name: s.sector,
    count: s.count,
    percent: s.percent,
    fill: s.count < 20 ? "#ef4444" : s.count < 50 ? "#f59e0b" : "#22c55e"
  }));

  const regionShortNames = {
    "North America": "N. America",
    "Europe": "Europe",
    "Asia Pacific": "Asia Pacific",
    "Sub-Saharan Africa": "Sub-Saharan",
    "Middle East & North Africa": "MENA",
    "Latin America & Caribbean": "LatAm & Carib.",
  };

  const regionData = gaps.region_stats.map(r => ({
    name: r.region,
    shortName: regionShortNames[r.region] || r.region,
    apps: r.total_apps,
    avgPerCountry: r.avg_per_country,
    countriesWithData: r.countries_with_data,
    totalCountries: r.total_countries,
    fill: r.avg_per_country < 3 ? "#ef4444" : r.avg_per_country < 8 ? "#f59e0b" : "#22c55e"
  }));

  return (
    <div className="bg-card border border-border rounded-lg p-5" data-testid="data-gaps-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-base font-semibold flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          Dataset Blind Spots
          <span className="text-xs font-normal text-muted-foreground ml-1">
            — Where our data falls short
          </span>
        </h3>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-xl font-bold" style={{ color: getCoverageColor(gaps.overall_coverage) }}>
              {gaps.overall_coverage}%
            </div>
            <div className="text-xs text-muted-foreground">Overall Coverage</div>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            data-testid="data-gaps-expand-btn"
          >
            {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Summary Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-red-400">{gaps.diversity_gaps.unknown_founder_pct}%</div>
          <div className="text-xs text-muted-foreground">Unknown Founders</div>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-amber-400">{gaps.diversity_gaps.female_founded_pct}%</div>
          <div className="text-xs text-muted-foreground">Female-Founded</div>
        </div>
        <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-violet-400">{gaps.diversity_gaps.lgbtq_founded_count}</div>
          <div className="text-xs text-muted-foreground">LGBTQIA+ Founded</div>
        </div>
        <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-cyan-400">{gaps.total_apps}</div>
          <div className="text-xs text-muted-foreground">Total Tracked</div>
        </div>
      </div>

      {/* Regional Coverage */}
      <div className="mb-5">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
          <MapPin className="w-3.5 h-3.5" /> Regional Coverage (avg apps per country)
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {regionData.map((r, i) => (
            <div key={i} className="bg-muted/20 border border-border rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1 truncate" title={r.name}>{r.shortName}</div>
              <div className="text-base font-bold" style={{ color: r.fill }}>{r.avgPerCountry}</div>
              <div className="text-xs text-muted-foreground">
                {r.countriesWithData}/{r.totalCountries} countries
              </div>
              <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${Math.min(100, (r.avgPerCountry / 15) * 100)}%`, backgroundColor: r.fill }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sector Gaps Chart */}
      <div className="mb-5">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
          <Layers className="w-3.5 h-3.5" /> Sector Coverage
        </h4>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={sectorData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
              <XAxis type="number" stroke={AXIS_STROKE} tick={AXIS_TICK} />
              <YAxis dataKey="name" type="category" stroke={AXIS_STROKE} width={110} tick={AXIS_TICK} interval={0} />
              <Tooltip
                {...TOOLTIP_STYLE}
                formatter={(value, name, props) => [`${value} apps (${props.payload.percent}%)`, 'Coverage']}
              />
              <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                {sectorData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Expanded: Country Gaps Table */}
      {expanded && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
            <Users className="w-3.5 h-3.5" /> Biggest Country Gaps (vs. expected AI ecosystem size)
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {gaps.country_gaps.filter(c => c.gap > 0).slice(0, 12).map((c, i) => (
              <div key={i} className="flex items-center justify-between bg-muted/20 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{c.country}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <span className="text-xs text-muted-foreground">{c.actual}/{c.expected}</span>
                  </div>
                  <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${c.coverage_percent}%`,
                        backgroundColor: getCoverageColor(c.coverage_percent)
                      }}
                    />
                  </div>
                  <span
                    className="text-xs font-semibold min-w-[40px] text-right"
                    style={{ color: getCoverageColor(c.coverage_percent) }}
                  >
                    {getCoverageLabel(c.coverage_percent)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataGapsCard;
